import { useMemo } from 'react';

import { useCurrentUserContext } from '.';

type CapabilityKey = keyof Omit<FleetAdminPermissions, '__typename'>;

interface UseFleetAdminPermissionsResult {
  /**
   * Capability flags for a specific OEM segment, or undefined if the
   * current user does not manage that segment.
   */
  forSegment(oemSegmentId: string): FleetAdminPermissions | undefined;

  /**
   * A single capability on a specific segment. Accepts `undefined` /
   * `null` so callers can pass an optional segment id (e.g. coming from
   * an unresolved context) directly. Returns a synthetic denied
   * capability with a `null` reason when the segment isn't one the
   * current user manages (or no segment was supplied); the UI
   * translator renders the generic "no permission" copy for that case.
   */
  capability(
    oemSegmentId: string | null | undefined,
    key: CapabilityKey,
  ): FleetAdminCapability;

  /**
   * For toolbar buttons not yet scoped to a specific segment. Allowed
   * if ANY managed segment grants the capability; otherwise denied
   * using a `deniedReason` taken from one of the segment-level reasons.
   */
  anySegment(key: CapabilityKey): FleetAdminCapability;
}

const SYNTHETIC_DENIED: FleetAdminCapability = {
  __typename: 'FleetAdminCapability',
  allowed: false,
  deniedReason: undefined,
};

export default function useFleetAdminPermissions(): UseFleetAdminPermissionsResult {
  const { oemSegments } = useCurrentUserContext();

  return useMemo(() => {
    const segments = oemSegments ?? [];
    const byId = new Map(segments.map((segment) => [segment.id, segment]));

    return {
      forSegment(id) {
        return byId.get(id)?.fleetAdminPermissions;
      },

      capability(id, key) {
        if (id == null) return SYNTHETIC_DENIED;
        const perms = byId.get(id)?.fleetAdminPermissions;
        return perms?.[key] ?? SYNTHETIC_DENIED;
      },

      anySegment(key) {
        if (segments.length === 0) return SYNTHETIC_DENIED;
        const allowed = segments.find(
          (segment) => segment.fleetAdminPermissions[key].allowed,
        );
        if (allowed) return allowed.fleetAdminPermissions[key];

        // All segments deny. Use the first denial reason — the BE phrasing
        // is generic ("Requires X on the sponsoring account") and works
        // regardless of which segment it came from.
        return segments[0].fleetAdminPermissions[key];
      },
    };
  }, [oemSegments]);
}
